import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LuBell as HiOutlineBell } from "react-icons/lu";
import { equalTo, get, orderByChild, push, query, ref } from 'firebase/database';
import { database } from '../constants/firebase';
import { isValidEmail } from '../utils/utils';

const ComingSoonPopup = ({ open, onClose }) => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);


    const handleNotify = async () => {
        const trimmedEmail = email.trim().toLowerCase();

        if (!trimmedEmail) {
            setError("Please enter your email.");
            return;
        }

        if (!isValidEmail(trimmedEmail)) {
            setError("Please enter a valid email address.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const waitlistRef = ref(database, "vip_waitlist");

            // Check if email already exists
            const emailQuery = query(
                waitlistRef,
                orderByChild("email"),
                equalTo(email.toLowerCase())
            );

            const snapshot = await get(emailQuery);

            if (snapshot.exists()) {
                setError("This email is already on the waitlist.");
                setLoading(false);
                return;
            }

            // Save new email
            await push(waitlistRef, {
                email: email.toLowerCase(),
                createdAt: Date.now(),
            });

            setEmail("");
            onClose();
            window.showToast("You're on the list! 🎉", 'success');

        } catch (err) {
            console.error(err);
            setError("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };



    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[1800] flex flex-col items-center justify-center px-4 sm:px-6"
                >
                    {/* Backdrop */}
                    <div className="absolute inset-0 bg-[#000]/70 backdrop-blur-md" />

                    {/* VIP Heading */}
                    <motion.h1
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="absolute top-6 sm:top-10 text-2xl sm:text-4xl font-bold text-white tracking-wider"
                    >
                        Coming Soon
                    </motion.h1>

                    {/* Center Content */}
                    <motion.div
                        initial={{ scale: 0.92, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0.95, opacity: 0 }}
                        transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                        className="relative w-full max-w-sm sm:max-w-xl mx-auto flex flex-col items-center text-center bg-transparent"
                    >
                        <HiOutlineBell className="w-16 h-16 sm:w-24 sm:h-24 mb-6 sm:mb-8 text-white" />
                        <h2 className="text-white text-2xl sm:text-3xl font-semibold mb-3 tracking-wide">
                            Launching Soon
                        </h2>
                        <p className="text-white/80 text-sm sm:text-base leading-relaxed max-w-md mb-6 px-2">
                            Be the first to know when we launch this section!
                        </p>

                        <input
                            type="email"
                            placeholder="Enter your email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={`w-full max-w-md px-4 py-3 rounded-xl bg-white/90 text-black placeholder-gray-500 outline-none ${error ? 'mb-1' : 'mb-4'}`}
                        />
                        {error && (
                            <p className="mt-1 text-red-400 text-sm mb-4">
                                {error}
                            </p>
                        )}

                        <button
                            onClick={handleNotify}
                            disabled={!email || loading}
                            className="w-full max-w-md bg-[#247295] hover:bg-[#1f6787] transition text-white font-semibold py-3 rounded-xl text-base shadow-lg"
                        >
                            {loading ? "Saving..." : "Notify Me"}
                        </button>

                        <button
                            onClick={onClose}
                            className="mt-4 sm:mt-6 text-white/60 hover:text-white text-xs sm:text-sm"
                        >
                            Maybe later
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ComingSoonPopup;
